import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip } from "recharts"
import { DisaggregatedMetric, GeoDetails, GeoTag } from "../../services/types"
import Skeleton from "react-loading-skeleton";
import 'react-loading-skeleton/dist/skeleton.css';
import { formatValue, formatValueBase } from "../../utils/helpers";
import { indexMetricIds } from "../../utils/dashboardUtils";
import { useDashboardColor } from "../../context/DashboardColorContext";
import MetricMetadataTrigger from "../metrics/MetricMetadataTrigger";

interface BarChartOnlyProps {
	metric: DisaggregatedMetric | undefined;
	disaggData: DisaggregatedMetric[] | null;
	geoDetails: GeoDetails[];
	geoTag: GeoTag | null;
	selectionActive: boolean;
	barColor?: string;
	height: string;
	background: string;
	showMetadataIcon?: boolean;
	topFive?: boolean;
	subText?: string;
	showValue?: boolean;
}

const normalizeLabel = (value: string) =>
  value
    .replace(/\u00A0/g, " ")   // NBSP → space
    .replace(/\s+/g, " ")      // collapse whitespace
    .replace(/\bNH\b/g, "Non-Hispanic")
    .trim();

// Helper for custom toolbar styling and value formatting (expand NH for race disaggregations)
const CustomTooltipBar = ({ active, payload, label }: any) => {
	if (active && payload && payload.length) {
		return (
		<div className="bg-white p-2 border border-slate-200 rounded shadow-sm text-xs">
			<p className="font-semibold">{`${normalizeLabel(label)}`}</p>
			<p>{`Value: ${formatValue(payload[0].value, payload[0].payload.unit)}`}</p>
		</div>
		)
	}
	return null
};

// Helper for more cleanly wrapping axis label text
const CustomTick = (props: any) => {
	const { x, y, payload } = props;
	const maxLineLength = 20; // max chars per line before wrapping
	const raw = typeof payload.value === "string" ? payload.value : String(payload.value);

	// normalize NBSP + collapse whitespace
	const label = raw
		.replace(/\u00A0/g, " ")
		.replace(/\s+/g, " ")
		.trim();

	const words = label.split(" ");

	// Helper to split words into lines <= maxLineLength chars
	const lines: string[] = [];
	let currentLine = '';

	words.forEach((word: any) => {
		if ((currentLine + word).length <= maxLineLength) {
		currentLine += (currentLine ? ' ' : '') + word;
		} else {
		if (currentLine) lines.push(currentLine);
		currentLine = word;
		}
	});
	if (currentLine) lines.push(currentLine);

	// If only one line, render single text, else render tspans with dy to wrap vertically
	if (lines.length === 1) {
		return (
		<text
			x={x}
			y={y}
			dy={2}
			textAnchor="end"
			fill="#666"
			style={{ whiteSpace: 'nowrap', fontSize: 10 }}
		>
			{lines[0]}
		</text>
		);
	}

	// Multiple lines: render each line in a <tspan> with vertical offset
	return (
		<text
		x={x}
		y={y}
		textAnchor="end"
		fill="#666"
		style={{ fontSize: 10 }}
		>
		{lines.map((line, i) => (
			<tspan key={i} x={x} dy={i === 0 ? -1 : 9}>
			{line}
			</tspan>
		))}
		</text>
	);
};

const MetricBarChart = ({
	metric,
	disaggData,
	geoDetails,
	geoTag,
	selectionActive,
	height,
	background,
	barColor = "#3B5C67",
	showMetadataIcon = true,
	topFive = false,
	subText,
	showValue = false,
}: BarChartOnlyProps) => {
	const color = useDashboardColor();

	// Wait for metric and data to be available
	if (!metric || !metric.versions || metric.versions.length === 0 || !metric.versions[0].disaggregations) {
		return <div className="h-24 flex items-center justify-center text-xs text-slate-500"><Skeleton height={24} width={"100%"}/></div>;
	}

	const id = metric.id;
	const name = metric.name;

	// Index metric?
	const isIndexMetric = indexMetricIds.includes(metric.id);

	const versions = metric?.versions || [];
	const disaggregations = versions[0].disaggregations;
	const unit = versions[0]?.unit;
	const value = versions[0]?.value;

	console.log('disaggregations for chart:', disaggregations);

	// Sort by highest value (treat missing values as 0)
	const sorted = [...disaggregations].sort(
	(a, b) => (b.value ?? 0) - (a.value ?? 0)
	);

	// Only keep top 5 if prop is true
	const filtered = topFive ? sorted.slice(0, 5) : sorted;

	const chartData = filtered.map(d => ({
		name: d.name,
		value: d.value ?? 0,
		unit,
	}));

	if (chartData.length === 0) {
		return <div className="h-24 flex items-center justify-center text-xs text-slate-500">No data available</div>
	}

	return (
	<>
	<div 
		className="relative overflow-hidden group hover:shadow-md transition-all border rounded-xl bg-white"
		style={{ 
			borderLeftWidth: isIndexMetric ? "4px" : undefined, borderLeftColor: isIndexMetric ? color : undefined,
		}}
	>
		<div className="p-3 bg-white">
			<div className="flex items-center justify-between">
				<div> 
					<h3 className="text-sm font-semibold text-port-text">
						{name}
					</h3>
					<div className="flex items-center">
					<p className="text-port-text text-xs mr-2">{subText}</p>
					<p className="text-port-text text-xs font-bold">
						{showValue && value && unit && (
							formatValueBase(value, unit)
						)}
					</p>
					</div>
					{isIndexMetric &&
                        <div className="mt-0.5 rounded-lg h-4 w-10 px-1.5 text-[10px] text-white text-center" style={{ backgroundColor: color }}>Index</div> 
                    }
				</div>
				
				<MetricMetadataTrigger
					metricId={id}
					metricName={name}
					disaggData={disaggData}
					geoDetails={geoDetails}
					geoTag={geoTag}
					selectionActive={selectionActive}
					showIcon={showMetadataIcon}
				/>
			</div>
		</div>

		<div className={`${height} ${background} min-h-[160px] min-w-0`}>
		<ResponsiveContainer width="100%" height="100%" minHeight={160}>
		<BarChart
			data={chartData}
			layout="vertical"
			margin={{ top: 2, right: 40, left: 15, bottom: 0 }}
			barCategoryGap="8%"
			barGap={-2}
		>
			<XAxis
				type="number"
				tick={{ fontSize: 10 }}
				tickFormatter={(value) => formatValue(value, unit, metric.id)}
			/>
			<YAxis
				type="category"
				dataKey="name"
				tick={<CustomTick/>}
				width={100}
				interval={0}
                tickLine={false}
			/>
			<Tooltip content={<CustomTooltipBar />} cursor={{fill: 'transparent'}} />
			<Bar dataKey="value" fill={barColor} activeBar={false} radius={[0, 4, 4, 0]} barSize={14} />
		</BarChart>
		</ResponsiveContainer>
		</div>

		<div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-[#2258e5]/50 to-[#00ac4e]/50 opacity-0 group-hover:opacity-100 transition-opacity" />
	</div>
	</>
	)
}

export default MetricBarChart;